import { useEffect, useRef, useState } from "react";
import Quill from "quill";
import "quill/dist/quill.snow.css";
import { useFormContext } from "react-hook-form";
import { RichEditorType } from "./RichEditorType";
import { toast } from "react-toastify";
import { Link } from "react-router";
import Signature from "../../../components/Signature";
import { hasGiftLinkPlaceholder } from "../../../utils/giftLinkPlaceholder";
import { prepareBodyForEditor } from "../../../utils/emailTemplateHtml";

interface RichEditorProps {
  name?: string;
  type?: string;
  signature?: string;
}

const RichEditor = ({ name = "body", type, signature = "" }: RichEditorProps) => {
  const { setValue, watch } = useFormContext();
  const editorRef = useRef<HTMLDivElement>(null);
  const quillInstanceRef = useRef<Quill | null>(null);
  const typeRef = useRef<string | undefined>(type);

  const bodyValue = watch(name);
  const previousValidHtmlRef = useRef<string>("");
  const applyingExternalValueRef = useRef(false);
  const [quillReady, setQuillReady] = useState(false);

  useEffect(() => {
    if (!editorRef.current || quillInstanceRef.current) return;



    const quill = new Quill(editorRef.current, {
      theme: "snow",


      modules: {
        toolbar: true
      },
    });

    quillInstanceRef.current = quill;
    quill.clipboard.dangerouslyPasteHTML(prepareBodyForEditor(bodyValue) || "");
    previousValidHtmlRef.current = prepareBodyForEditor(bodyValue) || "";
    setQuillReady(true);


    const container = editorRef.current;
    container.style.borderBottom = "1px solid rgba(0, 0, 0, 0.12)";
    container.style.color = "#222222 !important";
    container.style.boxShadow = "box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04)";

    if (type === RichEditorType.GIFT || type === RichEditorType.REMINDER) {
      container.style.borderBottom = "0";
    }


    // Add keyboard shortcuts for alignment
    quill.keyboard.addBinding({
      key: 'backspace',
      shortKey: true
    }, function (range: any) {
      // Handle backspace for alignment
      const [line] = quill.getLine(range.index);
      if (line) {
        const formats = line.formats();
        if (formats.align) {
          // Cycle through alignment options
          let newAlign = 'left';
          if (formats.align === 'left') newAlign = 'center';
          else if (formats.align === 'center') newAlign = 'right';
          else newAlign = 'left';

          quill.formatLine(range.index, 1, { align: newAlign });
          return false; // Prevent default backspace behavior
        }
      }
      return true; // Allow default backspace behavior
    });

    // Add space key binding for alignment
    quill.keyboard.addBinding({
      key: 'space',
      shortKey: true
    }, function (range: any) {
      // Handle space for alignment
      const [line] = quill.getLine(range.index);
      if (line) {
        const formats = line.formats();
        if (formats.align) {
          // Cycle through alignment options
          let newAlign = 'left';
          if (formats.align === 'left') newAlign = 'center';
          else if (formats.align === 'center') newAlign = 'right';
          else newAlign = 'left';

          quill.formatLine(range.index, 1, { align: newAlign });
          return false; // Prevent default space behavior
        }
      }
      return true; // Allow default space behavior
    });

    quill.keyboard.addBinding({
      key: 'l',
      shortKey: true
    }, function (range: any) {
      quill.formatLine(range.index, 1, { align: 'left' });
      return false;
    });


    quill.keyboard.addBinding({
      key: 'r',
      shortKey: true
    }, function (range: any) {
      quill.formatLine(range.index, 1, { align: 'right' });
      return false;
    });

    if (type === RichEditorType.SIGNATURE) {
      quill.root.style.direction = 'ltr';
      quill.root.style.textAlign = 'left';
    }

    quill.on("text-change", () => {
      const html = quill.root.innerHTML;
      const type = typeRef.current;

      // Accept programmatic updates from form setValue (e.g. email template selection).
      if (applyingExternalValueRef.current) {
        previousValidHtmlRef.current = html;
        setValue(name, html, { shouldDirty: true });
        return;
      }

      if (type !== RichEditorType.GIFT && type !== RichEditorType.EMAIL_TEMPLATE) {
        setValue(name, html, { shouldDirty: true });
        return;
      }

      if (hasGiftLinkPlaceholder(html)) {
        previousValidHtmlRef.current = html;
        setValue(name, html, { shouldDirty: true });
      }
      else {
        toast.error("You cannot remove the tag {gift_link}", {
          position: "top-right",
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        });
        quill.clipboard.dangerouslyPasteHTML(previousValidHtmlRef.current);
      }
    });

    return () => {
      quill.off("text-change", () => { });

      const toolbar = quill.getModule("toolbar") as { container?: HTMLElement };
      toolbar?.container?.remove();
      container.innerHTML = "";

      quillInstanceRef.current = null;
      setQuillReady(false);
    };
  }, [editorRef, setValue, name, type]);

  useEffect(() => {
    const quill = quillInstanceRef.current;
    if (!quill) return;

    const prepared = prepareBodyForEditor(bodyValue);
    if (prepared !== quill.root.innerHTML) {
      applyingExternalValueRef.current = true;
      quill.clipboard.dangerouslyPasteHTML(prepared || "");
      previousValidHtmlRef.current = prepared || "";
      // Quill fires text-change synchronously for paste; clear on next tick.
      queueMicrotask(() => {
        applyingExternalValueRef.current = false;
      });
    }
  }, [bodyValue, quillReady]);


  useEffect(() => {
    typeRef.current = type;
  }, [type]);

  return (
    <>


      {(type === RichEditorType.EMAIL_TEMPLATE || type === RichEditorType.REFERRAL || type === RichEditorType.MAILER) ?
        (<div
          style={{ fontSize: "14px", minHeight: "180px", height: "auto", borderBottom: "0", paddingBottom: "0", marginBottom: "0" }}
          className="quill-editor"
          ref={editorRef}
        />
        ) :
        (<div
          style={{ fontSize: "14px", minHeight: "180px", height: "auto", border: "1", borderBottom: "0", borderRadius: "0", paddingBottom: "0", marginBottom: "0" }}
          className="quill-editor"
          ref={editorRef}
        />

        )}
      {(type === RichEditorType.EMAIL_TEMPLATE || type === RichEditorType.REFERRAL || type === RichEditorType.MAILER) && (
        <div className="font-14 mt-1 italic">
          Please DO NOT add a signature. Your signature will be added
          automatically. You can update the signature from{" "}
          <Link to="/profile" className="link-text">
            profile
          </Link>
        </div>
      )}
      {
        (type === RichEditorType.GIFT || type === RichEditorType.REMINDER) && (
          <div style={{ marginTop: "-4px" }} className="font-14 mt-1 italic">
            <Signature isReadOnly={true} signature={signature}></Signature>
          </div>
        )
      }

    </>
  );
};

export default RichEditor;
